import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  TextField,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  FormControl,
  InputAdornment,
} from '@material-ui/core';
import { get } from 'lodash';
import styled from 'styled-components';
import { CheckCircle } from '@material-ui/icons';

import SahamDrawer from 'components/SahamDrawer';
import { translate, arrayTranslate } from 'containers/services/translate';
import messages from 'containers/PreOpeningPage/messages';
import { CLAIM } from 'containers/data-structure';
import { CHOICES } from 'containers/PreOpeningPage/constants';
import { Config } from 'containers/AppContainer/typings';
import validate from 'containers/services/validateForm';
import { displayErrorText } from 'containers/services/validateForm';
import { formsAreValidated } from 'containers/services/validateForm';
import { formatPourcentage } from 'containers/services/formatNumberInput';
import recapManagerMessages from 'containers/RecapManagerPage/messages';

export const claimCircumstancesConstraints = (claim: any) => {
  return {
    [CLAIM.RESPONSABILITY_SHARE]: {
      presence: true,
    },
    [CLAIM.ACCIDENT_DESCRIPTION]: {
      presence: true,
    },
    [CLAIM.IS_JUDICIAL_PROCEDURE]: {
      presence: true,
    },
    [CLAIM.JUDICIAL_PROCEDURE_ID]: {
      presence:
        get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], false) === CHOICES.YES,
    },
  };
};

export const claimCircumstancesValidator = (claim: any) => {
  return validate(claim, claimCircumstancesConstraints(claim));
};
export interface ClaimCircumstancesDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: Function;
  claimToPreopen: any;
  judicialProcedures: Array<Config>;
  setClaimRecap?: Function;
  closeClaimCircumstancesDrawer: Function;
}

const Container = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;

  .input {
    width: 327px;
    margin-bottom: 33px;
  }
  .label {
    font-size: 13px;
    font-family: Roboto;
  }
  .MuiFormLabel-root {
    font-size: 13px;
    color: #4f4f4f;
  }
  .MuiInputLabel-shrink {
    color: #00000061;
  }
  .MuiFormLabel-root.Mui-focused {
    color: rgba(9, 122, 194, 1);
  }
  .checkdiv {
    display: flex;
    flex-direction: column;
  }
  .subContainer {
    display: flex;
    flex-direction: row;
    margin-top: 15px;
  }
  .proceduresContainer {
    flex-direction: column;
    display: flex;
  }
  .text {
    text-align: left;
    font-size: 15px;
    color: #2b2727;
    margin-right: 17px;
    font-weight: Bold;
    font-family: Roboto;
  }
  .radio-label-style {
    font-size: 14px;
  }
  .error {
    margin-top: -5px;
    color: red;
    text-align: center;
  }
`;

const ClaimCircumstancesDrawer: React.FC<ClaimCircumstancesDrawerProps> = (
  props,
): JSX.Element => {
  const [claimToPreopen, updateClaimToPreopen] = useState(props.claimToPreopen);
  const [claimValidation, setClaimValidation] = React.useState({});

  /* istanbul ignore next */
  React.useEffect(() => {
    const expectedClaimToPreopen = { ...claimToPreopen };
    const expectedClaim = get(expectedClaimToPreopen, 'claim', {});
    const judicialProcedureId = get(expectedClaim, [
      CLAIM.JUDICIAL_PROCEDURE_ID,
    ]);
    const isJudicialProcedure = get(expectedClaim, [
      CLAIM.IS_JUDICIAL_PROCEDURE,
    ]);
    if (judicialProcedureId) {
      expectedClaim[CLAIM.IS_JUDICIAL_PROCEDURE] = isJudicialProcedure || true;
    } else {
      expectedClaim[CLAIM.IS_JUDICIAL_PROCEDURE] = isJudicialProcedure || false;
    }
    expectedClaimToPreopen.claim = expectedClaim;
    updateClaimToPreopen(expectedClaimToPreopen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* istanbul ignore next */
  const handleChange = (field: string) => (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    const expectedClaimToPreopen: any = { ...claimToPreopen };
    const { claim } = expectedClaimToPreopen;
    switch (field) {
      case CLAIM.IS_JUDICIAL_PROCEDURE:
        claim[CLAIM.IS_JUDICIAL_PROCEDURE] = value === 'true' ? true : false;
        break;
      case CLAIM.JUDICIAL_PROCEDURE_ID:
        claim[CLAIM.JUDICIAL_PROCEDURE_ID] = parseInt(value);
        break;
      case CLAIM.RESPONSABILITY_SHARE:
        claim[CLAIM.RESPONSABILITY_SHARE] = formatPourcentage(value);
        break;
      case CLAIM.ACCIDENT_DESCRIPTION:
        claim[CLAIM.ACCIDENT_DESCRIPTION] = value;
        break;
      default:
        return expectedClaimToPreopen;
    }
    updateClaimToPreopen(expectedClaimToPreopen);
  };

  /* istanbul ignore next */
  const handleValidateDrawer = () => () => {
    if (props.setClaimRecap) {
      const expectedClaim = get(claimToPreopen, 'claim', {});
      const validator = claimCircumstancesValidator(expectedClaim) || {};
      setClaimValidation(validator);
      if (formsAreValidated(validator)) {
        const expectedClaimToPreopen = { ...claimToPreopen };
        if (!get(expectedClaimToPreopen.claim, [CLAIM.IS_JUDICIAL_PROCEDURE])) {
          expectedClaimToPreopen.claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
        }
        props.setClaimRecap(expectedClaimToPreopen);
        props.closeClaimCircumstancesDrawer();
      }
    }
  };

  const { claim } = claimToPreopen;

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    claimValidation,
    translateArrayErrorMessages(claimValidation, true),
  );

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      title={translate({ ...messages.claimCircumstancesTitle }, props)}
      handleValidateDrawer={handleValidateDrawer()}
      confirmButtonTitle={translate(
        recapManagerMessages.drawerSaveFormButtonTitle,
        props,
      )}
    >
      <Container>
        <TextField
          id="responsibility"
          onChange={handleChange(CLAIM.RESPONSABILITY_SHARE)}
          name={CLAIM.RESPONSABILITY_SHARE}
          value={get(claim, [CLAIM.RESPONSABILITY_SHARE])}
          label={translate({ ...messages.responsibilty }, props)}
          className="input"
          error={!!get(claimValidation, [CLAIM.RESPONSABILITY_SHARE])}
          helperText={message[CLAIM.RESPONSABILITY_SHARE]}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {translate({ ...messages.responsibiltySharePercentage }, props)}
              </InputAdornment>
            ),
          }}
        ></TextField>
        <TextField
          id="description"
          onChange={handleChange(CLAIM.ACCIDENT_DESCRIPTION)}
          name={CLAIM.ACCIDENT_DESCRIPTION}
          value={get(claim, [CLAIM.ACCIDENT_DESCRIPTION])}
          label={translate({ ...messages.description }, props)}
          className="input"
          multiline
          error={!!get(claimValidation, [CLAIM.ACCIDENT_DESCRIPTION])}
          helperText={message[CLAIM.ACCIDENT_DESCRIPTION]}
        ></TextField>
        <div className="checkdiv">
          <Typography className="text">
            {translate({ ...messages.legalProcedure }, props)}
          </Typography>
          <div>
            <FormControl>
              <RadioGroup
                value={
                  !!get(
                    claim,
                    [CLAIM.IS_JUDICIAL_PROCEDURE],
                    get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], ''),
                  )
                }
                onChange={handleChange(CLAIM.IS_JUDICIAL_PROCEDURE)}
                row
              >
                <FormControlLabel
                  control={
                    <Radio
                      color="primary"
                      value={CHOICES.YES}
                      checkedIcon={<CheckCircle />}
                    />
                  }
                  label={translate({ ...messages.yes }, props)}
                />
                <FormControlLabel
                  control={
                    <Radio
                      color="primary"
                      value={CHOICES.NO}
                      checkedIcon={<CheckCircle />}
                    />
                  }
                  label={translate({ ...messages.no }, props)}
                />
              </RadioGroup>
              {/* istanbul ignore next */
              !!get(claimValidation, [CLAIM.IS_JUDICIAL_PROCEDURE]) && (
                <FormHelperText className="error">
                  {message[CLAIM.IS_JUDICIAL_PROCEDURE]}
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
        {!!get(
          claim,
          [CLAIM.IS_JUDICIAL_PROCEDURE],
          get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], ''),
        ) && (
          /* istanbul ignore next */
          <FormControl>
            <RadioGroup
              className="subContainer"
              aria-label="position"
              name="position"
              value={get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], '')}
              onChange={handleChange(CLAIM.JUDICIAL_PROCEDURE_ID)}
              row
            >
              <div className="proceduresContainer">
                {/* istanbul ignore next */
                props.judicialProcedures.map(
                  /* istanbul ignore next */
                  (procedure: Config) => {
                    return (
                      <FormControlLabel
                        key={procedure.id}
                        value={procedure.id}
                        classes={{ label: 'radio-label-style' }}
                        control={<Radio color="primary" />}
                        label={procedure.title}
                        labelPlacement="end"
                      />
                    );
                  },
                )}
              </div>
            </RadioGroup>

            {/* istanbul ignore next */
            !!get(claimValidation, [CLAIM.JUDICIAL_PROCEDURE_ID]) && (
              <FormHelperText className="error">
                {message[CLAIM.JUDICIAL_PROCEDURE_ID]}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </Container>
    </SahamDrawer>
  );
};

export default injectIntl(ClaimCircumstancesDrawer);
